(() => {
  if (window.__awSeriesFabricDetailLoaded) {
    return;
  }

  window.__awSeriesFabricDetailLoaded = true;

  const instances = new Map();
  const animationDuration = 300;
  const labelAnimationDuration = 120;
  const selectors = {
    root: "[data-aw-series-fabric-detail]",
    trigger: "[data-aw-series-fabric-detail-trigger]",
    label: "[data-aw-series-fabric-detail-label]",
    panel: "[data-aw-series-fabric-detail-panel]"
  };

  class AWSeriesFabricDetail {
    constructor(root) {
      this.root = root;
      this.trigger = root.querySelector(selectors.trigger);
      this.label = root.querySelector(selectors.label);
      this.panel = root.querySelector(selectors.panel);
      this.openLabel = this.trigger?.dataset.openLabel || "";
      this.closeLabel = this.trigger?.dataset.closeLabel || "";
      this.labelTimeout = null;
      this.onClick = this.onClick.bind(this);

      if (!this.trigger || !this.panel) {
        return;
      }

      this.bindEvents();
      this.setExpanded(false, false);
    }

    shouldAnimate() {
      return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    bindEvents() {
      this.trigger.addEventListener("click", this.onClick);
    }

    onClick(event) {
      event.preventDefault();
      this.setExpanded(this.trigger.getAttribute("aria-expanded") !== "true", true);
    }

    stopAnimation() {
      if (!this.panel) {
        return;
      }

      if (this.panel.__awSeriesFabricDetailTransitionEnd) {
        this.panel.removeEventListener("transitionend", this.panel.__awSeriesFabricDetailTransitionEnd);
        this.panel.__awSeriesFabricDetailTransitionEnd = null;
      }

      this.panel.style.transition = "none";
      this.panel.style.height = `${this.panel.offsetHeight}px`;
      void this.panel.offsetHeight;
    }

    resetPanelStyles() {
      if (!this.panel) {
        return;
      }

      this.panel.style.height = "";
      this.panel.style.overflow = "";
      this.panel.style.transition = "";
    }

    updateLabel(isExpanded, animate = false) {
      if (!this.label) {
        return;
      }

      const nextLabel = isExpanded ? this.closeLabel : this.openLabel;

      if (!animate || !this.shouldAnimate() || this.label.textContent === nextLabel) {
        this.label.classList.remove("is-updating");
        this.label.textContent = nextLabel;
        return;
      }

      if (this.labelTimeout) {
        window.clearTimeout(this.labelTimeout);
        this.labelTimeout = null;
      }

      this.label.classList.add("is-updating");

      this.labelTimeout = window.setTimeout(() => {
        this.label.textContent = nextLabel;

        requestAnimationFrame(() => {
          this.label.classList.remove("is-updating");
        });

        this.labelTimeout = null;
      }, labelAnimationDuration / 2);
    }

    setExpanded(isExpanded, animate = true) {
      if (!this.trigger || !this.panel) {
        return;
      }

      this.root.classList.toggle("is-open", isExpanded);
      this.trigger.setAttribute("aria-expanded", isExpanded ? "true" : "false");
      this.updateLabel(isExpanded, animate);

      if (isExpanded) {
        this.openPanel(animate);
        return;
      }

      this.closePanel(animate);
    }

    openPanel(animate) {
      this.stopAnimation();
      this.panel.hidden = false;

      if (!animate || !this.shouldAnimate()) {
        this.resetPanelStyles();
        return;
      }

      this.panel.style.overflow = "hidden";
      this.panel.style.height = "0px";
      this.panel.style.transition = `height ${animationDuration}ms ease`;

      requestAnimationFrame(() => {
        this.panel.style.height = `${this.panel.scrollHeight}px`;
      });

      const onTransitionEnd = (event) => {
        if (event.target !== this.panel || event.propertyName !== "height") {
          return;
        }

        this.panel.removeEventListener("transitionend", onTransitionEnd);
        this.panel.__awSeriesFabricDetailTransitionEnd = null;
        this.resetPanelStyles();
      };

      this.panel.__awSeriesFabricDetailTransitionEnd = onTransitionEnd;
      this.panel.addEventListener("transitionend", onTransitionEnd);
    }

    closePanel(animate) {
      this.stopAnimation();

      if (!animate || !this.shouldAnimate()) {
        this.panel.hidden = true;
        this.resetPanelStyles();
        return;
      }

      this.panel.hidden = false;
      this.panel.style.overflow = "hidden";
      this.panel.style.height = `${this.panel.scrollHeight}px`;
      this.panel.style.transition = `height ${animationDuration}ms ease`;

      requestAnimationFrame(() => {
        this.panel.style.height = "0px";
      });

      const onTransitionEnd = (event) => {
        if (event.target !== this.panel || event.propertyName !== "height") {
          return;
        }

        this.panel.removeEventListener("transitionend", onTransitionEnd);
        this.panel.__awSeriesFabricDetailTransitionEnd = null;
        this.panel.hidden = true;
        this.resetPanelStyles();
      };

      this.panel.__awSeriesFabricDetailTransitionEnd = onTransitionEnd;
      this.panel.addEventListener("transitionend", onTransitionEnd);
    }

    destroy() {
      if (!this.trigger || !this.panel) {
        return;
      }

      if (this.panel.__awSeriesFabricDetailTransitionEnd) {
        this.panel.removeEventListener("transitionend", this.panel.__awSeriesFabricDetailTransitionEnd);
        this.panel.__awSeriesFabricDetailTransitionEnd = null;
      }

      if (this.labelTimeout) {
        window.clearTimeout(this.labelTimeout);
        this.labelTimeout = null;
      }

      if (this.label) {
        this.label.classList.remove("is-updating");
      }

      this.trigger.removeEventListener("click", this.onClick);
    }
  }

  const init = (container = document) => {
    container.querySelectorAll(selectors.root).forEach((root) => {
      if (instances.has(root)) {
        return;
      }

      instances.set(root, new AWSeriesFabricDetail(root));
    });
  };

  const destroy = (container) => {
    container.querySelectorAll(selectors.root).forEach((root) => {
      const instance = instances.get(root);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(root);
    });
  };

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init(document));
  } else {
    init(document);
  }
})();
